import React from 'react';
import SummaryCard from 'src/shared/components/card-templates/SummaryCard';
import { Loader } from 'src/shared/components/loader/Loader';
import { useGetSiteThreatenedSpeciesQuery } from '../../state/api/siteNatureRiskEngineSlice';

type IucnRedListSummaryWidgetProps = {
    organisationBusinessCentralId: string;
    siteId: string;
};

// IUCN gradient, most-threatened (red) → near-threatened (green). LC is not
// shown — the engine doesn't return it for this widget.
const CATEGORY_COLOR: Record<string, string> = {
    CR: '#d84c3e',
    EN: '#e8863c',
    VU: '#edc84c',
    NT: '#a7d24f',
};

// Shares the list widget's query args so RTK serves both from one request.
const PREVIEW_SIZE = 5;

// The bar's two outer ends are fully rounded. Inner edges are square, except
// the first segment's inner (right) end, which gets a slight radius.
const OUTER_RADIUS = '9999px';
const INNER_RADIUS = '3000px';
const STANDARD = '2px';

const IucnRedListSummaryWidget = ({ organisationBusinessCentralId, siteId }: IucnRedListSummaryWidgetProps) => {
    const { data, isLoading } = useGetSiteThreatenedSpeciesQuery(
        { organisationBusinessCentralId, siteId, page: 1, pageSize: PREVIEW_SIZE },
        { skip: !organisationBusinessCentralId || !siteId }
    );

    if (isLoading) {
        return (
            <SummaryCard
                cardIcon={null}
                cardTitle='IUCN Red List Species'
                titleExplainerSlug={{ defaultSlug: 'iucn-status', explainerSlug: '' }}
                containerClasses='gap-4 p-5'
                cardBody={
                    <div className='flex min-h-[120px] items-center justify-center'>
                        <Loader />
                    </div>
                }
            />
        );
    }

    const summary = data?.siteThreatenedSpecies?.summary;
    if (!summary || summary.totalCount === 0) {
        return null;
    }

    // Only categories actually present get a proportional segment + label.
    const segments = summary.categories.filter(c => c.count > 0);

    return (
        <SummaryCard
            cardIcon={null}
            cardTitle='IUCN Red List Species'
            titleExplainerSlug={{ defaultSlug: 'iucn-status', explainerSlug: '' }}
            containerClasses='gap-4 p-5'
            cardBody={
                <div data-testid='iucn-red-list-summary-widget' className='flex flex-col gap-4'>
                    <div className='flex items-baseline gap-2'>
                        <span data-testid='iucn-threatened-count' className='text-4xl font-semibold'>
                            {summary.threatenedCount}
                        </span>
                        <span className='text-2xl text-grey-80'>threatened</span>
                    </div>

                    <div className='flex flex-col gap-1'>
                        <div className='flex h-2.5 gap-0.5' data-testid='iucn-summary-bar'>
                            {segments.map((c, idx) => {
                                const isFirst = idx === 0;
                                const isLast = idx === segments.length - 1;
                                return (
                                    <div
                                        key={c.category}
                                        style={{
                                            flexGrow: c.count,
                                            backgroundColor: CATEGORY_COLOR[c.category] ?? '#c9c9c9',
                                            borderTopLeftRadius: isFirst ? OUTER_RADIUS : isLast ? INNER_RADIUS : STANDARD,
                                            borderBottomLeftRadius: isFirst ? OUTER_RADIUS : isLast ? INNER_RADIUS : STANDARD,
                                            borderTopRightRadius: isLast ? OUTER_RADIUS : isFirst ? INNER_RADIUS : STANDARD,
                                            borderBottomRightRadius: isLast ? OUTER_RADIUS : isFirst ? INNER_RADIUS : STANDARD,
                                        }}
                                        title={`${c.category}: ${c.count}`}
                                    />
                                );
                            })}
                        </div>
                        <div className='flex gap-0.5 text-sm text-grey-80'>
                            {segments.map(c => (
                                <span key={c.category} style={{ flexGrow: c.count, flexBasis: 0 }} className='min-w-0'>
                                    {c.category}
                                </span>
                            ))}
                        </div>
                    </div>

                    <p className='text-sm text-grey-80'>{summary.totalCount} total IUCN Red List species</p>
                </div>
            }
        />
    );
};

export default IucnRedListSummaryWidget;
